import { isSet } from "../helpers/isset";
import EmblaCarousel from 'embla-carousel';
import { addPrevNextBtnsClickHandlers } from './embla-carousel-nav';
import ClassNames from 'embla-carousel-class-names';
/**
 * Description: Depot Finder Google Maps Setup
 * 
 * @class DepotFinder
 * @typedef {DepotFinder}
 */

class DepotFinder {
    constructor (domNode, MapLib, MarkerLib, InfoWindowLib) {
        this.map;

        this.domNode = domNode;
        this.dataUrl = domNode.getAttribute("data-fetch-url");
        this.mapElm = this.domNode.querySelector("#map");
        this.primaryDepotElm = this.domNode.querySelector(".depot-contact-box.primary");
        this.searchCol = this.domNode.querySelector(".search-depot-col");
        this.zoomOutControlElm = this.domNode.querySelector(".map-controls__zoom-out");
        this.zoomInControlElm = this.domNode.querySelector(".map-controls__zoom-in");
        this.resetControlElm = this.domNode.querySelector(".map-controls__reset");
        this.locationResultsContainer = this.domNode.querySelector('.results-container');

        //Google Map Libraries
        this.MapLib = MapLib;
        this.MarkerLib = MarkerLib;
        this.InfoWindowLib = InfoWindowLib;

        this.mapMarkers = [];
        this.mapWindows = [];
        this.locations = [];
        this.primarySlug = '';
        this.zoom = 7;
        this.tilt = 30;
        this.lng = -4.2180701859773;
        this.lat = 55.86634446616404;

        this.iconBase = "../assets2024/img/svg/icons/depot-finder/";
        this.icons = {
            info: {
                icon: this.iconBase + "pin-outline.svg",
            },
            current: {
                icon: this.iconBase + "pin-filled.svg",
            }
        };

        const zoom = this.zoom;
        const tilt = this.tilt;
        const lat = this.lat;
        const lng = this.lng;
        this.map = new this.MapLib(this.mapElm, {
            center: {
                lat: lat,
                lng: lng
            },
            zoom: zoom,
            tilt: tilt,
            mapId: 'fb19892b39e2bfa9',
            streetViewControl: false,
            clickableIcons: false,
            mapTypeControl: false,
            fullscreenControl: false,
            disableDefaultUI: true,
            scrollwheel: false
        });

        this.zoomOutControlElm.addEventListener('click', this.handleMapZoomOut.bind(this), false);
        this.zoomInControlElm.addEventListener('click', this.handleMapZoomIn.bind(this), false);
        this.resetControlElm.addEventListener('click', this.handleMapReset.bind(this), false);

        this.locationResultsContainer.addEventListener('loaded', this.initDepotLocations.bind(this), false);

        this.loadMapLocations(this.dataUrl);
    }


    async loadMapLocations(url){
        const jsonres = await fetch(url);
        const jsonList = await jsonres.json();
        this.jsonList = jsonList;
        this.createMapLocations(jsonList);
        this.updatePrimaryDepot(jsonList, this.primarySlug);
        this.initDepotLocations();
    }

    createMapLocations(locations){
        let primarySlug;

        locations.map((location) => {
            const lat = parseFloat(location.lat);
            const lng = parseFloat(location.lng);

            const locationObj = {
                "is_location": location.location,
                "location_slug": location.slug,
                "position": new google.maps.LatLng(lat, lng),
                "type": location.type,
                "content": `
                    <div class="gmap--info-window">
                        <h4>${location.title}</h4>
                        <div class="detail">
                            <span><i class="sa-icon--phone sa-brand"></i></span>
                            <span>:</span>
                            <span><a href="tel:${location.tel}">${location.tel}</a></span>
                        </div>
                        <button class="button info-window-trigger info-window-trigger--js" data-slug="${location.slug}">
                            <span>View Full Details</span>
                            <i class="sa-icon--arrow-right"></i>
                        </button>
                    </div>
                `
            };

            if(location.primary === "true"){
                primarySlug = location.slug;
            }

            this.locations.push(locationObj);
        });

        this.primarySlug = primarySlug;

        this.buildLocateButton();
        this.createMapMarkers();
    }

    createMapMarkers(){
        const locations = this.locations;
        const updatePrimaryDepot = this.updatePrimaryDepot.bind(this);
        const jsonList = this.jsonList;
        const primaryDepotElm = this.primaryDepotElm;
        let currentSlug = this.primarySlug;

        for (let i = 0; i < locations.length; i++) {
            const map = this.map;
            const location = locations[i];
            const icons = this.icons;

            if(!location.is_location && !location.is_location.length) return;

            const marker = new this.MarkerLib(
                {
                    marker_slug: location.location_slug,
                    position: location.position,
                    icon: icons.info.icon,
                    map: map,
                }
            );
            this.mapMarkers.push(marker);

            const window = new this.InfoWindowLib({
                content: location.content,
                window_slug: location.location_slug,
            });

            google.maps.event.addListener(
                window,
                'domready',
                function(){
                    if( isSet( primaryDepotElm.querySelector("[data-slug]")) ){
                        currentSlug = primaryDepotElm.querySelector("[data-slug]").getAttribute("data-slug");
                    }

                    const slug = this.anchor.marker_slug;
                    const updatePrimaryDepotHandler = (e) => updatePrimaryDepot(jsonList, slug, e);
                    const trigger = document.querySelector(".info-window-trigger--js");
                    const triggerSpan = trigger.querySelector('span');

                    if(currentSlug === slug){
                        trigger.setAttribute("disabled","");
                        triggerSpan.textContent = 'Currently Viewing';
                    }else{
                        trigger.removeAttribute("disabled");
                        triggerSpan.textContent = 'View Full Details';
                        trigger.addEventListener('click', updatePrimaryDepotHandler, false);
                    }
                }
            ); 

            this.mapWindows.push(window);

            const markerHandler = (e) => this.handleMapMarkerClick(marker, window, e);

            marker.addListener("click", markerHandler);
        }
    }
  
    initDepotLocations(){
        const handleDepotOptionClick = this.handleDepotOptionClick.bind(this);
        const depotLocations = this.domNode.querySelectorAll('.depot-action--js');

        const currentDepotSlug = this.primaryDepotElm.querySelector('[data-slug]').getAttribute('data-slug');

        depotLocations.forEach(function (location) {
            const depotResult = location.closest(".depot-result");
            const slug = location.getAttribute("data-slug");
            location.addEventListener('click', handleDepotOptionClick, false);
            
            if(slug === currentDepotSlug){
                depotResult.classList.add("current");
                location.textContent = "Currently Viewing";
            }
        });

        const depotResultSliderOpts = {
            dragFree: false,
            axis: "y",
            slidesToScroll: 1,
            skipSnaps: true
        };

        const depotResultSliderNode = this.domNode.querySelector('.embla');
        const depotResultSliderViewportNode = this.domNode.querySelector('.embla__viewport');

        const prevBtn = depotResultSliderNode.querySelector('.embla__button--prev');
        const nextBtn = depotResultSliderNode.querySelector('.embla__button--next');

        const depotResultSliderEmblaApi = EmblaCarousel(
            depotResultSliderViewportNode, 
            depotResultSliderOpts, 
            [
                ClassNames(
                    {
                        snapped: "",
                        draggable: "",
                        dragging: ""
                    }
                )
            ]
        );

        const removePrevNextBtnsClickHandlers = addPrevNextBtnsClickHandlers(
            depotResultSliderEmblaApi,
            prevBtn,
            nextBtn
        );

        depotResultSliderEmblaApi.on('destroy', removePrevNextBtnsClickHandlers);
    }

    handleDepotOptionClick(e){
        const map = this.map;
        const icons = this.icons;
        const locations = this.domNode.querySelectorAll('.depot-result');
        const resultElm = e.target.closest('.depot-result');
        const buttonElm = e.target;
        const slug = buttonElm.getAttribute("data-slug");

        locations.forEach(function (location) {
            location.classList.remove("current");
            location.querySelector("button").textContent = "View";
        });

        resultElm.classList.add("current");
        buttonElm.textContent = "Currently Viewing";
        this.updatePrimaryDepot(this.jsonList, slug);

        this.mapWindows.forEach(function (window){
            window.close();
        })

        this.mapMarkers.forEach(function (marker){
            marker.setIcon(icons.info.icon);

            if(marker.marker_slug === slug){
                map.panTo(marker.position);
                marker.setIcon(icons.current.icon);
            }
        });

        this.searchCol.scrollIntoView({ behavior: "smooth" });
    }

    handleMapZoomIn(){
        const map = this.map;
        map.setZoom(map.getZoom() + 1);
    }

    handleMapZoomOut(){
        const map = this.map;
        map.setZoom(map.getZoom() - 1);
    }

    handleMapReset(){
        const position = new google.maps.LatLng(this.lat, this.lng);

        this.map.setZoom(this.zoom);
        this.map.setTilt(this.tilt);
        this.map.panTo(position);
    }

    handleMapMarkerClick(marker, window, e){
        const map = this.map;
        const icons = this.icons;
        const markers = this.mapMarkers;
        const windows = this.mapWindows;

        for (let i = 0; i < windows.length; i++) {
            const window = windows[i];
            window.close();
        }

        for (let i = 0; i < markers.length; i++) {
            const marker = markers[i];
            marker.setIcon(icons.info.icon);
        }

        marker.setIcon(icons.current.icon);

        map.panTo(
            marker.position
        );

        window.open({
            anchor: marker,
            map,
            shouldFocus: false,
        });

        google.maps.event.addListener(map, 'click', function() {
            window.close();
            marker.open = false;
        });
    }

    buildLocateButton(){
        if (!navigator.geolocation) return;

        const button = document.createElement('button');
        button.classList.add('map-controls__locate', 'button', 'square');

        const icon = document.createElement('i');
        icon.classList.add('sa-icon--locate');

        button.append(icon);
        const mapControlsContainer = document.querySelector('.map-controls');

        button.addEventListener('click', this.handleLocateButtonClick.bind(this), false);

        mapControlsContainer.append(button);
    }

    getDistance(userLat, userLng, depotLat, depotLng, unit) {
        if ((userLat == depotLat) && (userLng == depotLng)) {
            return 0;
        } else {
            const radUserlat = Math.PI * userLat/180;
            const radDepotLat = Math.PI * depotLat/180;
            const theta = userLng-depotLng;
            const radTheta = Math.PI * theta/180;
            let dist = Math.sin(radUserlat) * Math.sin(radDepotLat) + Math.cos(radUserlat) * Math.cos(radDepotLat) * Math.cos(radTheta);
            
            if (dist > 1) {
                dist = 1;
            }

            dist = Math.acos(dist);
            dist = dist * 180/Math.PI;
            dist = dist * 60 * 1.1515;

            if (unit=="K") { 
                dist = dist * 1.609344 
            }

            if (unit=="N") { 
                dist = dist * 0.8684 
            }

            return dist;
        }
    }

    async getUserLocation() {
        const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
        });
      
        return {
            lng: pos.coords.longitude,
            lat: pos.coords.latitude,
        };
    }

    findClosestDepotLocation(userLocation){
        const locations = this.locations;
        const user = userLocation;
        let closestLocation = "";
        let closestLocationDistance = ""

        for (let i = 0; i < locations.length; i++) {
            let location = locations[i];

            let userLat = user.lat;
            let userLng = user.lng;
            let locationLat = location.position.lat();
            let locationLng = location.position.lng();

            let distance = this.getDistance(userLat, userLng, locationLat, locationLng);

            if(!location.is_location && !location.is_location.length) continue;

            if(closestLocation == "" || closestLocationDistance == ""){
                closestLocation = location;
                closestLocationDistance = distance;
                continue;
            }

            if(distance < closestLocationDistance){
                closestLocation = location;
                closestLocationDistance = distance;
            }
        }

        this.closestLocation = closestLocation;
    }

    async handleLocateButtonClick(){
        if(!isSet(this.closestLocation)){
            const userLocation = await this.getUserLocation();
            this.findClosestDepotLocation(userLocation);
        }

        const map = this.map;
        const markers = this.mapMarkers;
        const windows = this.mapWindows;
        const icons = this.icons;
        let closestWindow;
        let closestMarker;

        for (let i = 0; i < windows.length; i++) {
            const infoWindow = windows[i];
            infoWindow.close();
            
            if(this.closestLocation.location_slug == infoWindow.window_slug){
                closestWindow = infoWindow;
            }
        }

        for (let i = 0; i < markers.length; i++) {
            const marker = markers[i];
            marker.setIcon(icons.info.icon);

            if(this.closestLocation.location_slug == marker.marker_slug){
                closestMarker = marker;
            }
        }

        closestMarker.setIcon(icons.current.icon);

        map.panTo(
            closestMarker.position
        );

        map.setZoom(10);

        closestWindow.open({
            anchor: closestMarker,
            map,
            shouldFocus: false,
        });

        google.maps.event.addListener(map, 'click', function() {
            closestWindow.close();
            closestMarker.open = false;
        });

        this.updatePrimaryDepot(this.jsonList, this.closestLocation.location_slug);
    }

    updatePrimaryDepot(jsonList, depotSlug, e){
        const primaryDepotElm = this.primaryDepotElm;

        jsonList.forEach(function (item) {
            if(item.slug !== depotSlug) return;

            const primaryBoxFragment = document.createDocumentFragment();

            const contentsElement = document.createElement("div");
            contentsElement.classList.add("card__content");

            const depotTitleElement = document.createElement("h3");
            depotTitleElement.classList.add("depot-title");
            depotTitleElement.textContent = item.title;
            contentsElement.appendChild(depotTitleElement);

            const details = [
                {
                    "key": "tel",
                    "icon": "sa-icon--phone",
                    "value": item.tel
                },
                {
                    "key": "email",
                    "icon": "sa-icon--email",
                    "value": item.email
                },
                {
                    "key": "address",
                    "icon": "sa-icon--home",
                    "value": item.address
                },
                {
                    "key": "hours",
                    "icon": "sa-icon--calendar",
                    "value": item.opening_hours
                }
            ];

            const detailsElement = document.createElement("div");
            detailsElement.classList.add("card__details", "card--depot__details");

            details.forEach((detail) => {
                if(!isSet(detail.value)) return;

                const detailElement = document.createElement("div");
                detailElement.classList.add("card__detail", "card--depot__detail");

                const iconElement = document.createElement("i");
                iconElement.classList.add(detail.icon);
                detailElement.appendChild(iconElement);

                if(detail.key != "hours"){
                    let valueElement;

                    if(detail.key == "tel" || detail.key == "email"){
                        valueElement = document.createElement("a");
                    }else{
                        valueElement = document.createElement("span");
                    }

                    if(detail.key == "email"){
                        valueElement.href = "mailto:" + detail.value;
                    }

                    if(detail.key == "tel"){
                        valueElement.href = "tel:" + detail.value;
                    }
                    valueElement.textContent = detail.value;
                    detailElement.appendChild(valueElement);
                }else{
                    const valueContainerElement = document.createElement("div");
                    valueContainerElement.classList.add("inner-items");

                    Object.entries(detail.value).forEach(([key,value]) => {
                        const valueElement = document.createElement("div");
                        const labelElement = document.createElement("span");
                        const seperatorElement = document.createElement("span");
                        const innerValueElement = document.createElement("span");
                        

                        if(key === "monfri"){
                            key = "mon - fri";
                        }
                        labelElement.textContent = key.charAt(0).toUpperCase() + key.slice(1);
                        seperatorElement.textContent = " : ";
                        innerValueElement.textContent = value;

                        valueElement.appendChild(labelElement);
                        valueElement.appendChild(seperatorElement);
                        valueElement.appendChild(innerValueElement);

                        valueContainerElement.appendChild(valueElement);
                    });

                    detailElement.appendChild(valueContainerElement);
                }
                
                detailsElement.append(detailElement);
            });

            contentsElement.appendChild(detailsElement);
            contentsElement.setAttribute("data-slug", item.slug);
            primaryBoxFragment.appendChild(contentsElement);

            primaryDepotElm.innerHTML = "";
            primaryDepotElm.appendChild(primaryBoxFragment);
        });

        const windowTrigger = document.querySelector(".info-window-trigger--js");
        if(isSet(windowTrigger)){
            windowTrigger.setAttribute("disabled","");
            const windowTriggerSpan = windowTrigger.querySelector('span');
            windowTriggerSpan.textContent = 'Currently Viewing';
        }

        const depotSearchLocations = this.domNode.querySelectorAll('.depot-result');

        depotSearchLocations.forEach(function (depot){
            const button = depot.querySelector("button");
            const slug = button.getAttribute("data-slug");

            depot.classList.remove("current");
            button.textContent = "View";

            if(slug === depotSlug){
                depot.classList.add("current");
                button.textContent = "Currently Viewing";
            }
        });

        if(isSet(e) && e.type == "click"){
            this.searchCol.scrollIntoView({ behavior: "smooth" });
        }
    }
}

exports.DepotFinder = DepotFinder